import { useEffect, useRef, type ReactNode } from 'react';
import { ReactLenis, type LenisRef } from 'lenis/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registra o plugin ScrollTrigger uma única vez.
gsap.registerPlugin(ScrollTrigger);

interface SmoothScrollProviderProps {
  children: ReactNode;
}

/**
 * Smooth scroll global via Lenis, sincronizado com o ticker do GSAP
 * para que ScrollTrigger e o Lenis trabalhem em perfeita harmonia.
 *
 * Respeita prefers-reduced-motion automaticamente.
 *
 * Curva e duração foram calibradas para uma sensação cinematográfica
 * sem ficar lenta ou pesada.
 */
export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
  const lenisRef = useRef<LenisRef>(null);

  useEffect(() => {
    // Sincroniza o Lenis com o ticker do GSAP em vez de usar o RAF interno.
    // Isso garante que ScrollTrigger.update() rode no mesmo frame do scroll.
    function update(time: number) {
      lenisRef.current?.lenis?.raf(time * 1000);
    }

    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(update);
    };
  }, []);

  // Detecta preferência por movimento reduzido.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <ReactLenis
      root
      ref={lenisRef}
      options={{
        // Quando reduced motion está ativo, scroll praticamente instantâneo.
        lerp: prefersReducedMotion ? 1 : 0.1,
        duration: 1.2,
        // Easing exponencial — sensação premium ao soltar o scroll.
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: !prefersReducedMotion,
        // Lenis controla o frame pelo GSAP ticker (acima).
        autoRaf: false,
        // Permite scroll nativo em modais e elementos com data-lenis-prevent.
        allowNestedScroll: true,
      }}
    >
      {children}
    </ReactLenis>
  );
}
